import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import userAdminService from '../../services/userAdminService';

const NOTA_PASSWORD =
    'La password la genera il sistema e viene mostrata una volta sola, subito dopo la ' +
    'creazione: non è recuperabile. Nessuna email viene inviata — va consegnata a mano.';

// Non si nomina il sistema di identità: chi usa Automate non deve sapere quale sia, e
// nominarlo renderebbe la frase falsa il giorno in cui cambia. Basta che sappia che quella
// strada esiste e che non passa da qui.
const NOTA_RUOLI =
    'La persona nasce senza permessi e finché non gliene dai non vede nulla. Puoi assegnarle ' +
    'i ruoli dal suo pannello, subito dopo averla creata, oppure aggiungerla a un gruppo ' +
    'dalla pagina Gruppi.';

/**
 * Dialogo «Nuovo utente» del tab Utenti (spec §9).
 *
 * Non c'e' campo password (§6/D7): sceglierla a mano porta alla stessa password per tutti.
 * Non c'e' scelta di ruoli: l'account nasce senza permessi e li riceve dal pannello che
 * esiste gia', cosi' l'assegnazione resta un gesto solo, tracciato dove gia' lo era.
 *
 * I campi seguono le regole del realm, lette all'apertura:
 * - il «Nome utente» compare solo se il realm non usa l'email come username: con
 *   quell'impostazione il sistema di identita' scarta lo username ricevuto e salva l'email, e
 *   chiederlo farebbe comunicare alla persona un nome con cui non entrera' mai;
 * - nome e cognome sono obbligatori se il profilo utente del realm li esige: senza, la persona
 *   verrebbe fermata al primo accesso finche' non li inserisce lei.
 * Finche' la risposta non arriva «Crea» resta disabilitato; se la lettura fallisce si torna ai
 * campi di prima (nome utente obbligatorio, nome e cognome facoltativi), e il backend applica
 * comunque le regole del realm.
 */
export default function NewUserDialog({ open, onClose, onCreate }) {
    const [campi, setCampi] = useState({ username: '', email: '', firstName: '', lastName: '' });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    // null = non ancora note
    const [regole, setRegole] = useState(null);

    useEffect(() => {
        if (!open) {
            return undefined;
        }
        setCampi({ username: '', email: '', firstName: '', lastName: '' });
        setError(null);
        setSaving(false);
        setRegole(null);
        let attivo = true;
        userAdminService.getCreationSettings().then(
            (impostazioni) =>
                attivo &&
                setRegole({
                    emailComeUsername: Boolean(impostazioni?.emailAsUsername),
                    nomeObbligatorio: Boolean(impostazioni?.firstNameRequired),
                    cognomeObbligatorio: Boolean(impostazioni?.lastNameRequired),
                }),
            () =>
                attivo &&
                setRegole({ emailComeUsername: false, nomeObbligatorio: false, cognomeObbligatorio: false }),
        );
        return () => {
            attivo = false;
        };
    }, [open]);

    const emailComeUsername = regole?.emailComeUsername === true;
    const chiedeUsername = regole?.emailComeUsername === false;
    const nomeObbligatorio = regole?.nomeObbligatorio === true;
    const cognomeObbligatorio = regole?.cognomeObbligatorio === true;

    const cambia = (campo) => (event) =>
        setCampi((precedenti) => ({ ...precedenti, [campo]: event.target.value }));

    const handleSubmit = () => {
        setSaving(true);
        setError(null);
        onCreate({
            ...(chiedeUsername ? { username: campi.username.trim() } : {}),
            email: campi.email.trim(),
            firstName: campi.firstName.trim(),
            lastName: campi.lastName.trim(),
        }).then(
            () => setSaving(false),
            (err) => {
                setSaving(false);
                // Il messaggio del backend dice *quale* condizione manca — username gia'
                // preso, federazione in sola lettura (422), password policy — e inghiottirlo
                // lascerebbe l'amministratore senza sapere come uscirne.
                setError(
                    err?.response?.data?.message ||
                        'Non è stato possibile creare l’utente.',
                );
            },
        );
    };

    const completo =
        regole !== null &&
        campi.email.trim() !== '' &&
        (!chiedeUsername || campi.username.trim() !== '') &&
        (!nomeObbligatorio || campi.firstName.trim() !== '') &&
        (!cognomeObbligatorio || campi.lastName.trim() !== '');

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Nuovo utente</DialogTitle>
            <DialogContent dividers>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    {chiedeUsername && (
                        <TextField
                            label="Nome utente"
                            value={campi.username}
                            onChange={cambia('username')}
                            required
                            fullWidth
                            autoFocus
                        />
                    )}
                    <TextField
                        label="Email"
                        type="email"
                        value={campi.email}
                        onChange={cambia('email')}
                        required
                        fullWidth
                        autoFocus={!chiedeUsername}
                        helperText={
                            emailComeUsername
                                ? 'È anche il nome con cui la persona entra in Automate.'
                                : undefined
                        }
                    />
                    <TextField
                        label="Nome"
                        value={campi.firstName}
                        onChange={cambia('firstName')}
                        required={nomeObbligatorio}
                        fullWidth
                    />
                    <TextField
                        label="Cognome"
                        value={campi.lastName}
                        onChange={cambia('lastName')}
                        required={cognomeObbligatorio}
                        fullWidth
                    />
                    <Typography variant="body2" color="text.secondary">
                        {NOTA_PASSWORD}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {NOTA_RUOLI}
                    </Typography>
                    {error && <Alert severity="error">{error}</Alert>}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Annulla</Button>
                <Button variant="contained" onClick={handleSubmit} disabled={!completo || saving}>
                    Crea
                </Button>
            </DialogActions>
        </Dialog>
    );
}

NewUserDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    // Restituisce una Promise: il dialogo resta aperto col messaggio del backend se la
    // creazione fallisce, invece di chiudersi facendo credere che sia andata.
    onCreate: PropTypes.func.isRequired,
};
